import { useMemo } from 'react';
import {
  TablePlanAssistantChange,
  TablePlanAssistantTableChange,
} from './useTablePlanAssistant';
import { FloorPlan, Reservation, ReservationStatus } from 'types/reservations';
import { ReservationShift } from 'types/shifts';
import { blockToTime } from 'utils/helper';

const sortTables = (tables: string[] = []) => [...tables].sort().join('|');

const sameTables = (a: string[] = [], b: string[] = []) =>
  sortTables(a) === sortTables(b);

const getShiftBounds = (shifts: ReservationShift[]) => {
  const starts = shifts
    .filter((shift) => shift.active && !shift.commentOnly)
    .map((shift) => shift.start)
    .filter((block) => typeof block === 'number');
  const closes = shifts
    .filter((shift) => shift.active && !shift.commentOnly)
    .map((shift) => shift.close)
    .filter((block) => typeof block === 'number');

  const start = starts.length ? Math.min(...starts) : 0;
  const close = closes.length ? Math.max(...closes) : start + 8;

  return { start, close: close > start ? close : start + 8 };
};

const getTableSpace = (tables: FloorPlan['tables'], tableName: string) =>
  tables.find((table) => table.name === tableName)?.space ?? '';

const createPreviewBlock = ({
  date,
  restaurantId,
  tableName,
  space,
  start,
  close,
}: {
  date: string;
  restaurantId: string;
  tableName: string;
  space: string;
  start: number;
  close: number;
}) =>
  ({
    id: `table-plan-assistant-block-${tableName}`,
    restaurant: restaurantId,
    date,
    time: blockToTime(start),
    startTimeInBlocks: start,
    endTimeInBlocks: close,
    reservationLength: close - start,
    space,
    tables: [tableName],
    tableStr: tableName,
    guest: {
      name: 'Blockiert',
      email: '',
      phone: '',
      comment: '',
      attr: [],
    },
    guests: 0,
    fixed: true,
    blocked: true,
    blockedFullShift: true,
    status: ReservationStatus.SUCCESS,
    currentStatus: 'normal',
    source: 'TablePlanAssistant',
    position: 0,
    previousTables: null,
    tablePlanAssistantChanged: true,
  } as Reservation);

export const applyTablePlanAssistantReservationPreview = ({
  reservations,
  changes,
  tables,
  shifts,
  date,
  restaurantId,
}: {
  reservations: Reservation[];
  changes: TablePlanAssistantChange[];
  tables: FloorPlan['tables'];
  shifts: ReservationShift[];
  date: string;
  restaurantId: string | null;
}) => {
  if (!changes.length || !restaurantId) return reservations;

  const tableChanges = changes.filter(
    (change): change is TablePlanAssistantTableChange =>
      change.type === 'tableChanged'
  );
  const reservationUpdates = tableChanges.reduce(
    (acc, change) => ({ ...acc, [change.reservationId]: change }),
    {} as Record<string, TablePlanAssistantTableChange>
  );

  const finalBlockedTables = new Set<string>();

  reservations.forEach((reservation) => {
    if (reservation.blocked && reservation.blockedFullShift) {
      (reservation.tables ?? []).forEach((table) => finalBlockedTables.add(table));
    }
  });

  changes.forEach((change) => {
    if (change.type === 'block') {
      change.tables.forEach((table) => finalBlockedTables.add(table));
    }

    if (change.type === 'unblock') {
      change.tables.forEach((table) => finalBlockedTables.delete(table));
    }
  });

  const updatedReservations = reservations
    .filter((reservation) => {
      if (!reservation.blocked || !reservation.blockedFullShift) return true;

      return (reservation.tables ?? []).some((table) =>
        finalBlockedTables.has(table)
      );
    })
    .map((reservation) => {
      const update = reservationUpdates[reservation.id];

      if (!update) return reservation;

      return {
        ...reservation,
        tables: update.after.tables,
        space: update.after.space,
        previousTables: sameTables(update.before.tables, update.after.tables)
          ? reservation.previousTables ?? null
          : update.before.tables,
        tablePlanAssistantChanged: true,
      } as Reservation;
    });

  const existingBlockedTables = new Set<string>();
  updatedReservations.forEach((reservation) => {
    if (reservation.blocked && reservation.blockedFullShift) {
      (reservation.tables ?? []).forEach((table) => existingBlockedTables.add(table));
    }
  });

  const { start, close } = getShiftBounds(shifts);
  const syntheticBlocks = Array.from(finalBlockedTables)
    .filter((tableName) => !existingBlockedTables.has(tableName))
    .map((tableName) =>
      createPreviewBlock({
        date,
        restaurantId,
        tableName,
        space: getTableSpace(tables, tableName),
        start,
        close,
      })
    );

  return [...updatedReservations, ...syntheticBlocks];
};

export const applyTablePlanAssistantShiftPreview = (
  shifts: ReservationShift[] | null,
  changes: TablePlanAssistantChange[]
) => {
  if (!shifts || !changes.length) return shifts;

  return shifts.map((shift) => {
    const closedSpaces = new Set<string>(shift.overwrite?.closedSpaces ?? []);

    changes.forEach((change) => {
      if (change.type === 'closeSpace') {
        closedSpaces.add(change.space);
      }

      if (change.type === 'openSpace') {
        closedSpaces.delete(change.space);
      }
    });

    return {
      ...shift,
      overwrite: {
        ...shift.overwrite,
        closedSpaces: Array.from(closedSpaces),
      },
    };
  });
};

const useTablePlanAssistantPreview = ({
  reservations,
  shifts,
  tables,
  changes,
  date,
  restaurantId,
}: {
  reservations: Reservation[];
  shifts: ReservationShift[] | null;
  tables: FloorPlan['tables'];
  changes: TablePlanAssistantChange[];
  date: string;
  restaurantId: string | null;
}) => {
  const previewShifts = useMemo(
    () => applyTablePlanAssistantShiftPreview(shifts, changes),
    [changes, shifts]
  );

  const previewReservations = useMemo(
    () =>
      applyTablePlanAssistantReservationPreview({
        reservations,
        changes,
        tables,
        shifts: previewShifts ?? [],
        date,
        restaurantId,
      }),
    [changes, date, previewShifts, reservations, restaurantId, tables]
  );

  return {
    reservations: previewReservations,
    shifts: previewShifts,
  };
};

export default useTablePlanAssistantPreview;
